import { Box, UnorderedList, OrderedList, ListItem } from '@chakra-ui/react';
import { parseDate, formatDate } from '../../../utilities/temporal-utils';
import { ApplicantOfferConditionType, ApplyScholarship, IApplicantOffer, IOfferLetter, ScholarshipType } from 'api-paths-cas';
import { TextWithLinks } from './../../atoms/TextWithLinks';

interface IOfferLetterProps {
  offerLetter: IOfferLetter;
  programme: IApplicantOffer['programme'];
  pendingChanges?: IApplicantOffer['pendingChanges'];
  backgroundColor?: string;
  studentNo?: string;
}

export const OfferLetter: React.FC<IOfferLetterProps> = ({
  offerLetter,
  programme,
  pendingChanges = {},
  backgroundColor = 'chakra-subtle-bg',
  studentNo,
}) => {
  const { conditions, assess, givenName } = offerLetter;

  const conditionalOffer = conditions.length > 0;

  let startText = pendingChanges?.offerLetter?.startText ?? offerLetter?.startText;
  const endText = pendingChanges?.offerLetter?.endText ?? offerLetter?.endText;

  if (startText) {
    startText = modifyStartTextConditionType(startText, conditionalOffer);
  }

  // Fields that can be edited via pendingChanges without creating a new offer
  const entryPoint = pendingChanges?.programme?.entryPoint ?? programme?.entryPoint;
  const startDate = pendingChanges?.programme?.startDate ?? programme?.startDate;
  const endDate = pendingChanges?.programme?.endDate ?? programme?.endDate;
  const fee = pendingChanges?.programme?.fee ?? programme?.fee;
  const feeTotal = pendingChanges?.programme?.feeTotal ?? programme?.feeTotal;
  const scholarships = pendingChanges?.programme?.scholarships ?? programme?.scholarships;
  const campus = pendingChanges?.programme?.campus ?? programme?.campus;

  return (
    <Box py={3} px={4} backgroundColor={backgroundColor} mt={2} borderRadius="10">
      {conditionalOffer && <Box mb={4}>{`CONDITIONAL OFFER`}</Box>}
      {studentNo && <Box mb={4}>{`STUDENT NUMBER: ${studentNo}`}</Box>}

      <Box mb={4}>Dear, {givenName}</Box>

      {startText?.split('\n').map((line, i) => (
        <Box key={i}>{line}</Box>
      ))}

      {(scholarships ?? []).length > 0 && (
        <Box mt={2} mb={2}>
          <ScholarshipDisplay scholarships={scholarships!} fee={fee!} />
        </Box>
      )}

      <Box mt={4} mb={4}>
        Full details of the course are as follows:
      </Box>

      <UnorderedList mb={4}>
        <ListItem>
          Course: <b>{programme?.courseName || 'Course information not available'}</b>
        </ListItem>
        {programme?.awardingBody && (
          <ListItem>
            Awarding body: <b>{programme.awardingBody}</b>
          </ListItem>
        )}
        <ListItem>
          Mode: <b>{programme?.mode || 'Mode not specified'}</b>
        </ListItem>
        {campus && (
          <ListItem>
            Campus: <b>{campus}</b>
          </ListItem>
        )}
        <ListItem>
          Entry level: <b>{entryPoint}</b>
        </ListItem>
        {startDate && (
          <ListItem>
            Start date: <b>{parseDate(startDate) ? formatDate(parseDate(startDate)!, 'D MMM YYYY') : 'Invalid date'}</b>
          </ListItem>
        )}
        {endDate && (
          <ListItem>
            End date: <b>{parseDate(endDate) ? formatDate(parseDate(endDate)!, 'D MMM YYYY') : 'Invalid date'}</b>
          </ListItem>
        )}
        {fee && (
          <ListItem>
            {'Course fee (year 1): '} <b>£{fee.toLocaleString()}</b>
          </ListItem>
        )}
        {feeTotal && (
          <ListItem>
            Total course fees: <b>£{feeTotal.toLocaleString()}</b>
          </ListItem>
        )}
        {(scholarships ?? []).length > 0 && (
          <ListItem>
            Scholarships: <b>{scholarships!.map(s => formatApplyScholarship(s, fee!)).join(' & ')}</b>
          </ListItem>
        )}
      </UnorderedList>

      {conditions.length > 0 && (
        <>
          <Box mb={4}>You are now required to satisfy the following conditions:</Box>
          <OrderedList mb={4}>
            {conditions.map((condition, index) => (
              <ListItem key={index}>
                <TextWithLinks text={condition} />
              </ListItem>
            ))}
          </OrderedList>
        </>
      )}

      {assess?.length > 0 && (
        <>
          <Box mb={4}>
            {`This offer is ${
              conditions.length ? 'also' : ''
            } conditional to your provided information meeting the following requirements of the
            course:`}
          </Box>
          <UnorderedList mb={4}>
            {assess.map((assessReq, index) => (
              <ListItem key={index}>{assessReq}</ListItem>
            ))}
          </UnorderedList>
        </>
      )}

      <Box mb={4}>Regards,</Box>

      {endText?.split('\n').map((line, i) => (
        <Box key={i}>{line}</Box>
      ))}
    </Box>
  );
};

interface ScholarshipDisplayProps {
  scholarships: ApplyScholarship[];
  fee?: number;
  intro?: string;
  outro?: string;
}

export const ScholarshipDisplay: React.FC<ScholarshipDisplayProps> = ({
  scholarships = [],
  fee = 0,
  intro = 'This programme includes',
  outro = 'towards your study fees.',
}) => {
  if (!scholarships || scholarships.length === 0) {
    return null;
  }

  return (
    <>
      {intro}{' '}
      {scholarships.length === 1
        ? formatApplyScholarship(scholarships[0], fee)
        : scholarships.map((scholarship, index) => (
            <>
              {index > 0 && index === scholarships.length - 1 ? ' and ' : ''}
              {index > 0 && index < scholarships.length - 1 ? ', ' : ''}
              {formatApplyScholarship(scholarship, fee)}
            </>
          ))}{' '}
      {outro}
    </>
  );
};

export const formatApplyScholarship = (scholarship: ApplyScholarship, fee: number = 0) => {
  if (scholarship.type === ScholarshipType.PERCENTAGE) {
    const calculatedAmount = fee ? Math.round(fee * (scholarship.percentage / 100)) : 0;
    return `£${calculatedAmount.toLocaleString()} (${scholarship.percentage}%) ${scholarship.name}`;
  }
  return `£${scholarship.amount.toLocaleString()} ${scholarship.name}`;
};

export function modifyStartTextConditionType(startText: string, conditionalOffer: boolean): string {
  let finalText = startText;
  const conditionalTextWithSpace = ` a ${ApplicantOfferConditionType.Conditional} `;
  const unconditionalTextWithSpace = ` an ${ApplicantOfferConditionType.Unconditional} `;
  if (conditionalOffer) {
    finalText = startText.replace(unconditionalTextWithSpace, conditionalTextWithSpace);
  } else {
    finalText = startText.replace(conditionalTextWithSpace, unconditionalTextWithSpace);
  }
  return finalText;
}
